import { Combobox, ComboboxPopup, ComboboxWidget } from '@angular/aria/combobox';
import { Listbox, Option } from '@angular/aria/listbox';
import { CdkConnectedOverlay, OverlayModule } from '@angular/cdk/overlay';
import {
  afterRenderEffect,
  booleanAttribute,
  Component,
  computed,
  input,
  linkedSignal,
  model,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { FormValueControl } from '@angular/forms/signals';
import { filterOptions } from './select-options';
import { SelectOption } from './select.type';

let nextId = 0;

@Component({
  selector: 'app-select',
  imports: [Combobox, ComboboxPopup, ComboboxWidget, Listbox, Option, OverlayModule],
  templateUrl: './select.component.html',
  styleUrl: './select.component.scss',
})
export class SelectComponent<V> implements FormValueControl<V | null> {
  value = model<V | null>(null);

  options = input.required<SelectOption<V>[]>();

  label = input('');

  placeholder = input('Select');

  searchable = input(false, { transform: booleanAttribute });

  searchPlaceholder = input('Search...');

  clearable = input(false, { transform: booleanAttribute });

  matchWidth = input(true, { transform: booleanAttribute });

  disabled = input(false, { transform: booleanAttribute });

  required = input(false, { transform: booleanAttribute });

  invalid = input(false);

  touched = input(false);

  touch = output<void>();

  protected combobox = viewChild(Combobox);

  private listbox = viewChild(Listbox);

  private overlay = viewChild(CdkConnectedOverlay);

  protected labelId = `app-select-label-${nextId++}`;

  protected expanded = signal(false);

  protected query = linkedSignal({ source: this.expanded, computation: () => '' });

  protected visibleOptions = computed(() =>
    this.searchable() ? filterOptions(this.options(), this.query()) : this.options(),
  );

  protected selectedValues = computed(() => {
    const value = this.value();

    return value === null ? [] : [value];
  });

  private selectedLabel = computed(() => this.options().find((option) => Object.is(option.value, this.value()))?.label);

  protected hasSelection = computed(() => this.selectedLabel() !== undefined);

  protected displayText = computed(() => this.selectedLabel() ?? this.placeholder());

  protected inputPlaceholder = computed(() => (this.expanded() ? this.searchPlaceholder() : this.displayText()));

  protected emptyText = computed(() => (this.query() ? 'No matches' : 'No options'));

  protected showClear = computed(() => this.clearable() && this.hasSelection() && !this.disabled());

  protected hasError = computed(() => this.invalid() && this.touched());

  protected labelledBy = computed(() => (this.label() ? this.labelId : null));

  protected ariaLabel = computed(() => (this.label() ? null : this.placeholder()));

  constructor() {
    afterRenderEffect(() => this.listbox()?.scrollActiveItemIntoView());

    afterRenderEffect(() => {
      if (!this.expanded() || !this.listbox()) return;

      this.visibleOptions();
      this.overlay()?.overlayRef?.updatePosition();
    });
  }

  protected select(values: V[]) {
    this.value.set(values.at(0) ?? null);
  }

  protected open() {
    if (!this.disabled()) this.expanded.set(true);
  }

  protected close() {
    this.expanded.set(false);
  }

  protected clear() {
    this.value.set(null);
    this.close();
  }
}
